<template>
    <div class="EidCriterion">
        <RbacDisableGuard
            v-slot="{ isDisabledByRbac }"
            :features="[RbacFeature.decisioningManageCriteria]"
        >
            <EidJsonSchemaForm
                :schema="schema"
                :initial-value="initialValue"
                :default-value="defaultValue"
                :custom-component-input="getJsonSchemaCustomComponentInput"
                :root-properties-wrapper="rootPropertiesWrapper"
                :touched="true"
                :validate-on-mount="true"
                :rbac-disabled="isDisabledByRbac"
                @input="onInput"
                @update:visible-schemas="onUpdateVisibleSchemas"
            />
        </RbacDisableGuard>
    </div>
</template>

<style lang="scss">
    // stylelint-disable selector-max-combinators, selector-max-compound-selectors, selector-max-class
    $insured-field-checkbox-column-width: 85px;

    .EidCriterion {
        .EidJsonSchemaForm {
            .CriterionSettingsRow {
                &:first-child {
                    @apply pt-2.5;
                }

                @apply pb-2.5;

                &--substitutable {
                    .CriterionObjectInput.CriterionObjectInput--substitutable {
                        .CriterionEnumInput,
                        .CriterionCurrencyInput,
                        .CriterionTextInput {
                            max-width: initial;
                        }
                    }

                    .CriterionEnumInput,
                    .CriterionCurrencyInput,
                    .CriterionTextInput {
                        max-width: calc(100% - $insured-field-checkbox-column-width);
                    }
                }
            }
        }

        // this is a hack that needs to be removed if we decide to continue using this component
        // however we have plan to move away from jsonschema form and plus this component is only used by
        // a couple of criterion right now we should be fine until then
        [id$="isBasedOnInsuredField"] .Checkbox__content::after {
            content: "Set to Field";
        }
    }
</style>

<script lang="ts">
    import { defineComponent, markRaw, PropType } from 'vue';

    import EidJsonSchemaForm
        from '@evidentid/dashboard-commons/components/EidJsonSchemaForm/EidJsonSchemaForm.vue';
    import { FormErrors } from '@evidentid/dashboard-commons/components/EidJsonSchemaForm/types';
    import RbacDisableGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisableGuard.vue';
    import { createValueValidatorAllErrors } from '@evidentid/json-schema/createValueValidator';
    import { JsonSchemaObject } from '@evidentid/json-schema/interfaces/JsonSchema';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CriterionTemplate } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import { getJsonSchemaCustomComponentInput }
        from '@/modules/decisioning-criteria/components/CriterionInputs/criterionJsonSchemaFormElementsMap';
    import CriterionSettingsRow
        from '@/modules/decisioning-criteria/components/CriterionSettingsRow/CriterionSettingsRow.vue';
    import { LoadCriterionMessage } from '@/modules/decisioning-criteria/models/CriterionSettingLoadMessageFunc.model';
    import {
        EditableRequirementCriterion,
    } from '@/modules/decisioning-criteria/models/EditableRequirements.model';
    import { buildCriterionSchema } from '@/modules/decisioning-criteria/schemas/buildCriterionSchema';
    import { requirementTypeFieldReferenceDelimiter } from '@/modules/decisioning-criteria/types';
    import { eidStandardizeCriterionSchema } from '@/modules/decisioning-criteria/utils/eidStandardizeCriterionSchema';
    import { stripNullValues } from '@/modules/decisioning-criteria/utils/stripNullValues';

    export default defineComponent({
        name: 'EidCriterion',
        components: {
            EidJsonSchemaForm,
            RbacDisableGuard,
        },
        props: {
            criterion: {
                type: Object as PropType<EditableRequirementCriterion>,
                required: true,
            },
            template: {
                type: Object as PropType<CriterionTemplate>,
                required: true,
            },
            customProperties: {
                type: Array as PropType<CustomProperty[]>,
                default: (): CustomProperty[] => [] as CustomProperty[],
            },
            loadCriterionMessage: {
                type: Function as PropType<LoadCriterionMessage>,
                required: true,
            },
        },
        emits: [ 'controller-value-update', 'input', 'reference-accessed', 'update:visible-schemas' ],
        data() {
            return {
                RbacFeature,
                // markRaw: `data` is reactive, and wrapping a component definition in a proxy
                // makes Vue warn about the needless overhead when it resolves the wrapper.
                rootPropertiesWrapper: markRaw(CriterionSettingsRow),
                schema: eidStandardizeCriterionSchema(
                    buildCriterionSchema(
                        this.criterion.references,
                        Object.keys(this.template.evaluator.references) || [],
                        this.template,
                    ),
                    this.customProperties,
                ) as JsonSchemaObject,
                initialValue: stripNullValues({ ...this.criterion.references }),
                defaultValue: Object.entries(this.template.evaluator.references)
                    .reduce((acc, [ key, ref ]) => {
                        acc[key] = ref.value;
                        return acc;
                    }, {} as Record<string, any>),
            };
        },
        created() {
            const validator = createValueValidatorAllErrors(this.schema);
            validator(this.initialValue);
            const isValid = validator.errors ? validator.errors.length > 0 : true;
            this.loadCriterionMessage(isValid, this.criterion);
            this.$emit('controller-value-update', this.initialValue);
        },
        methods: {
            getJsonSchemaCustomComponentInput,
            onInput({
                formValue,
                formErrors,
                propValue,
                propKey,
                propErrors,
            }: {
                formValue: Record<string, any>;
                formErrors: FormErrors;
                propValue: any;
                propKey: string;
                propErrors: FormErrors;
            }): void {
                const updatedCriterion: EditableRequirementCriterion = {
                    ...this.criterion,
                    references: { ...this.criterion.references, [propKey]: propValue },
                };
                const reference = [
                    this.criterion.coverageType,
                    this.criterion.field,
                    propKey,
                ].join(requirementTypeFieldReferenceDelimiter);
                this.$emit('input', updatedCriterion);
                this.$emit('reference-accessed', { reference, isValid: !propErrors?.length });
                this.$emit('controller-value-update', formValue);
                this.loadCriterionMessage(!formErrors?.length, updatedCriterion);
            },
            onUpdateVisibleSchemas(visibleSchemas: string[]) {
                this.$emit('update:visible-schemas', visibleSchemas);
            },
        },
    });
</script>
